import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, ChevronUp, Bell } from "lucide-react";
import { auth } from "../../config/firebase";
import { signOut } from "firebase/auth";
//import { unloadZendesk } from "utils/loadZendesk";
import { motion, AnimatePresence, Variants } from "framer-motion";
import { easeIn, easeInOut } from "framer-motion";
import { clearUserUnitCoData } from "../../hooks/useUnitJwt";
import { resetUnitCoSession } from "../../utils/unitcoReset";
import { useArtist } from "../../contexts/ArtistContext";
import SurveyButton from "../Survey/SurveyButton";

const Navbar: React.FC<{ isAuthenticated: boolean; onSidebarToggle?: () => void; inSidebarToggle?: (open: boolean) => void }> = ({ isAuthenticated, onSidebarToggle, inSidebarToggle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  // Get pending invitations from ArtistContext
  const artistContext = useArtist();
  const allPendingInvitations = artistContext?.pendingInvitations || [];
  // Filter to only show active team invitations in the count
  const pendingInvitations = allPendingInvitations.filter(
    (inv) => inv.teamIsActive === true
  );
  // Old dropdown state - commented out for new design
  // const [showDropdown, setShowDropdown] = useState(false);
  // New dropdown states for MARKETS, KNOWLEDGE, LABS
  const [showMarketsDropdown, setShowMarketsDropdown] = useState(false);
  const [showKnowledgeDropdown, setShowKnowledgeDropdown] = useState(false);
  const [showLabsDropdown, setShowLabsDropdown] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  // Hover state for MARKETS dropdown items
  const [hoveredMarketsItem, setHoveredMarketsItem] = useState<string | null>(null);
  // Hover state for KNOWLEDGE dropdown items
  const [hoveredKnowledgeItem, setHoveredKnowledgeItem] = useState<string | null>(null);
  // Hover state for LABS dropdown items
  const [hoveredLabsItem, setHoveredLabsItem] = useState<string | null>(null);
  const handleSignOut = async () => {
    try {
      setIsSigningOut(true);
      const currentUser = auth.currentUser;
      const userId = currentUser?.uid;
      
      // Clear user-specific UnitCo data before clearing all localStorage
      if (userId) {
        clearUserUnitCoData(userId);
      }
      
      // Comprehensive UnitCo session reset
      await resetUnitCoSession();
      
      //Clear all session storages and localStorages BEFORE signOut
      window.sessionStorage.clear();
      window.localStorage.clear();
      
      await signOut(auth);
      
      //unloadZendesk(); // hide and remove Zendesk widget
      
      // Force browser reload to completely reset UnitWhiteLabelApp and all state
      window.location.href = "/login";
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };
  const dropdownVariants: Variants = {
    hidden: {
      opacity: 0,
      transition: {
        duration: 0.2,
        ease: easeInOut,
      },
    },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.2,
        delay: 0.18,
        ease: easeInOut,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.2,
        ease: easeIn,
      },
    },
  };

  const mobileMenuVariants: Variants = {
    hidden: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: easeInOut,
      },
    },
    visible: {
      height: "calc(100vh - 70px)",
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: easeInOut,
      },
    },
    exit: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: easeInOut,
      },
    },
  };

  const mobileDropdownVariants: Variants = {
    hidden: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: easeInOut,
      },
    },
    visible: {
      height: "auto",
      opacity: 1,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 200,
        mass: 0.5,
      },
    },
    exit: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: easeInOut,
      },
    },
  };
  


  return (
    <>
      <style>{`
        @media (min-width: 768px) {
          .mobile-nav-nova {
            padding-left: ${isAuthenticated ? '3%' : '7%'} !important;
            padding-right: ${isAuthenticated ? '3%' : '7%'} !important;
          }
        }
        @media (max-width: 767px) {
          header.navbar-menu-item {
            z-index: 9999 !important;
          }
        }
      `}</style>
    <header 
      className="relative w-full bg-nova-color navbar-menu-item px-4 py-4 md:py-0" 
      style={{
        position: 'sticky', 
        top: '0', 
        zIndex: 1000,
        paddingTop: isAuthenticated ? '1rem' : undefined,
        paddingBottom: isAuthenticated ? '1rem' : undefined,
        borderBottom: '1px solid #e5e7eb'
      }}
    >
      
      <div className={`mx-auto px-0 md:px-4 lg:px-8 xl:px-10 2xl:px-14 $
        {!isAuthenticated ? 'py-4 md:py-0' : ''} mobile-nav-nova`} style={{paddingLeft: "0px", paddingRight: "0px"}}>
        <div className="flex items-center justify-between ">
          
          {/* Left Section */}
          <div className="flex items-center md:flex-initial flex-1 md:flex-none" style={{marginLeft: '-2px', marginTop: '5px'}}>
            {/* Mobile Hamburger Menu - Left (Only for authenticated users) */}
            {isAuthenticated && !isSigningOut && (
              <button
                onClick={() => {
                  // const newState = !isSidebarOpen;
                  // setIsSidebarOpen(newState);
                  // if (inSidebarToggle) {
                  //   inSidebarToggle(newState);
                  // }
                  onSidebarToggle?.()
                }}
                className="md:hidden flex items-center justify-center mr-3"
                aria-label="Menu"
                style={{
                  width: '24px',
                  height: '24px',
                  padding: 0,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                <div className="flex flex-col justify-between" style={{ width: '20px', height: '16px' }}>
                  <span style={{ 
                    display: 'block', 
                    width: '100%', 
                    height: '2px', 
                    backgroundColor: '#000', 
                    borderRadius: '1px',
                    transition: 'all 0.3s'
                  }}></span>
                  <span style={{ 
                    display: 'block', 
                    width: '100%', 
                    height: '2px', 
                    backgroundColor: '#000', 
                    borderRadius: '1px',
                    margin: '4px 0',
                    transition: 'all 0.3s'
                  }}></span>
                  <span style={{ 
                    display: 'block', 
                    width: '100%', 
                    height: '2px', 
                    backgroundColor: '#000', 
                    borderRadius: '1px',
                    transition: 'all 0.3s'
                  }}></span>
                </div>
              </button>
            )}

            {/* Logo */}
            {isAuthenticated && !isSigningOut ? (
              <Link to="/" className="flex-shrink-0 md:flex-shrink-0">
                <img
                // src="/assets/images/new-mars-logo.svg"
                  // src="/assets/images/nova-new-logo.svg"
                  src="/assets/images/nova-logo.png"
                  // src="/assets/images/Nova.png"
                  alt="Nova Logo"
                  className="object-contain w-[55px] h-[24px]"
                />
              </Link>
            ) : (
              <Link to="/" className="flex-shrink-0 md:flex-shrink-0">
                <img
                  src="/assets/images/new-mars-logo.svg"
                  // src="/assets/images/nova-new-logo.svg"
                  alt="Mars Logo"
                  // className="object-contain w-[55px] h-[24px]"
                />
              </Link>
            )}
            
            {/* New Design: MARS Text - Commented out */}
            {/* <Link to="/" className="flex-shrink-0">
              <span className="text-black font-bold text-xl uppercase tracking-tight">MARS</span>
            </Link> */}
          </div>

          {/* Center Section - Navigation (Only for unauthenticated users) */}
          {!isAuthenticated && (
            
            
            <nav className="hidden md:flex items-center space-x-8 flex-1 justify-center relative" style={{paddingLeft: '9%'}}>
              {/* New Navigation: MARKETS, KNOWLEDGE, LABS */}
              {/* Note: Old small dropdown divs (positioned relative to each button) were replaced with these buttons */}
              {/* The full-width dropdown blocks now appear below the header (see lines 282-351) */}
              
              {/* MARKETS Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setShowMarketsDropdown(true)}
                onMouseLeave={() => setShowMarketsDropdown(false)}
                style={{
                  cursor: 'pointer',
                  padding: '1.5rem 0rem'
                }}
              >
                
                <button 
                  className={`uppercase transition-colors ${
                    showMarketsDropdown 
                      ? 'text-[#8B4513]' // Reddish-brown color when active
                      : 'text-black hover:text-gray-700'
                  }`}
                  style={{
                    fontFamily: 'Messinasans, Arial, sans-serif',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    letterSpacing: '2px',margin: '0px'
                  }}
                >
                  MARKETS {showMarketsDropdown ? <ChevronUp size={14} className="inline-block" /> : <ChevronDown size={14} className="inline-block" />}
                </button>
                
                
              </div>

              {/* KNOWLEDGE Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setShowKnowledgeDropdown(true)}
                onMouseLeave={() => setShowKnowledgeDropdown(false)}
                style={{
                  cursor: 'pointer',
                  padding: '1.5rem 0rem 1.6rem 1.7rem',
                  margin:'0px'
                  
                }}
              >
                
                <button 
                  className={`uppercase transition-colors ${
                    showKnowledgeDropdown 
                      ? 'text-[#8B4513]' // Reddish-brown color when active
                      : 'text-black hover:text-gray-700'
                  }`}
                  style={{
                    fontFamily: 'Messinasans, Arial, sans-serif',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    letterSpacing: '2px',margin: '0px'
                  }}
                >
                  KNOWLEDGE {showKnowledgeDropdown ? <ChevronUp size={14} className="inline-block" /> : <ChevronDown size={14} className="inline-block" />}
                </button>
                
                
              </div>

              {/* LABS Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setShowLabsDropdown(true)}
                onMouseLeave={() => setShowLabsDropdown(false)}
                style={{
                  cursor: 'pointer',
                  padding: '1.5rem 0rem 1.6rem 1.7rem',
                  margin:'0px'
                }}
              >
                
                <button 
                  className={`uppercase transition-colors ${
                    showLabsDropdown 
                      ? 'text-[#8B4513]' // Reddish-brown color when active
                      : 'text-black hover:text-gray-700'
                  }`}
                  style={{
                    fontFamily: 'Messinasans, Arial, sans-serif',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    letterSpacing: '2px',
                  }}
                >
                  LABS {showLabsDropdown ? <ChevronUp size={14} className="inline-block" /> : <ChevronDown size={14} className="inline-block" />}
                </button>
                
                
              </div>

              
            </nav>
            
          )}

          {/* Right Section - Buttons */}
          <div className="hidden md:flex items-center gap-6" style ={{marginTop: '4px'}}>
            {isAuthenticated ? (
              <>
                {/* Notification Icon - Only show if there are pending invitations */}
                {pendingInvitations && pendingInvitations.length > 0 && (
                  <button
                    onClick={() => navigate('/invitations')}
                    className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors"
                    aria-label="Notifications"
                  >
                    <Bell size={20} className="text-black" />
                    {/* Badge showing count */}
                    <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 bg-red-500 text-white text-xs font-semibold rounded-full">
                      {pendingInvitations.length}
                    </span>
                  </button>
                )}
                {/* Survey feedback button - only shows when an active survey exists */}
                <SurveyButton />
                <button
                  className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-full signout-btn"
                  onClick={handleSignOut}
                >
                  <svg width={20} height={20} viewBox="0 0 20 20" fill="none">
                    <path d="M4.1665 16.6667V15.8333C4.1665 12.6117 6.77818 10 9.99984 10C13.2215 10 15.8332 12.6117 15.8332 15.8333V16.6667" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9.99984 9.99992C11.8408 9.99992 13.3332 8.5075 13.3332 6.66659C13.3332 4.82564 11.8408 3.33325 9.99984 3.33325C8.15889 3.33325 6.6665 4.82564 6.6665 6.66659C6.6665 8.5075 8.15889 9.99992 9.99984 9.99992Z" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="">SIGN OUT</span>
                </button>
              </>
            ) : (
              <>
                {/* New Button: INVESTOR LOGIN */}
                <Link to="/login">
                  <button className="bg-black text-white px-6 py-2.5 rounded-full uppercase text-sm font-medium tracking-wide"
                  style={{
                    fontFamily: 'Messinasans,Arial,sans-serif',
                    fontSize: '0.625rem',
                    lineHeight: '140%',
                    fontWeight: 600,
                    letterSpacing: '2px',
                    borderRadius: '99px',
                    padding: '0.8rem 1.5rem',
                    border: '3px solid black'
                  }}
                  >
                    JOIN WAITLIST
                  </button>
                </Link>
                
               
              </>
            )}
          </div>

          {/* Mobile Notification Icon - Only show if authenticated and has pending invitations */}
          {isAuthenticated && pendingInvitations && pendingInvitations.length > 0 && (
            <button
              onClick={() => navigate('/invitations')}
              className="md:hidden relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors mr-2"
              aria-label="Notifications"
            >
              <Bell size={20} className="text-black" />
              {/* Badge showing count */}
              <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 bg-red-500 text-white text-xs font-semibold rounded-full">
                {pendingInvitations.length}
              </span>
            </button>
          )}
          
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`md:hidden flex items-center rounded-full flex-shrink-0 ${
              isAuthenticated ? 'px-4 py-4' : 'px-6 py-4 gap-2 bg-black text-white'
            }`}
            aria-label={isOpen ? "Close Menu" : "Open Menu"}
            style={{
              fontFamily: 'Messinasans, Arial, sans-serif',
              fontSize: '0.625rem',
              fontWeight: 600,
              letterSpacing: '2px',
              borderRadius: '99px',
              background: isAuthenticated ? 'transparent' : undefined,
              border: isAuthenticated ? 'none' : undefined,
            }}
          >
            {!isAuthenticated && (
              <span className="uppercase">{isOpen ? 'CLOSE' : 'MENU'}</span>
            )}
            {isOpen ? (
              <X size={20} className={isAuthenticated ? "text-black" : "text-white"} />
            ) : (
              <Menu size={20} className={isAuthenticated ? "text-black" : "text-white"} />
            )}
          </button>
        </div>
      </div>

      {/* Full-width Dropdown Blocks - Appears below header on hover */}
      {!isAuthenticated && (
        <>
        <div className="hidden md:flex items-center space-x-8 flex-1 justify-center relative novatesting">

</div>
          {/* Persistent Background Container - Prevents login form from showing through */}
          <AnimatePresence>
            {(showMarketsDropdown || showKnowledgeDropdown || showLabsDropdown) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.05 }}
                className="hidden md:block absolute top-full left-0 right-0 w-full bg-nova-color z-30"
                style={{ minHeight: '500px' }}
              />
            )}
          </AnimatePresence>
          {/* MARKETS Dropdown Block */}
          <AnimatePresence>
            {showMarketsDropdown && (
              <motion.div
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={dropdownVariants}
                className="hidden md:block absolute top-full left-0 right-0 w-full bg-nova-color shadow-lg z-40"
                onMouseEnter={() => setShowMarketsDropdown(true)}
                onMouseLeave={() => {
                  setShowMarketsDropdown(false);
                  setHoveredMarketsItem(null);
                }}
                style={{paddingLeft: "4%", paddingRight: "4%"}}
              >
                
                <div 
                  className="w-full mx-auto py-20 max-w-screen-2xl"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '.5fr .75fr',
                    gridColumnGap: '0rem',
                    gridRowGap: '0rem',
                    gridTemplateRows: 'auto',
                    gridAutoColumns: '1fr',
                    justifyContent: 'space-between',
                    paddingLeft: "5%",
                    paddingRight: "5%",
                  }}
                >
                 
                  <div 
                    style={{
                      display: 'flex',
                      flexFlow: 'column',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      width: '100%',
                      paddingRight: '2rem',
                      fontFamily: 'Messinasans, Arial, sans-serif',
                    }}
                  >
                    {/* MARKETS Label */}
                    <div>
                      
                      <div className="text-xs uppercase tracking-wider text-gray-600 font-medium" style={{
                        fontFamily: 'Messinasans, Arial, sans-serif',
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        letterSpacing: '2px',
                        marginBottom: '1.5rem',
                      }}>
                        MARKETS
                      </div>
                      
                      
                      <div className="space-y-2 mt-4">
                        
                        <a 
                          href="https://www.marsmarkets.com/markets-for-investors"
                          onMouseEnter={() => setHoveredMarketsItem('investors')}
                          onMouseLeave={() => setHoveredMarketsItem(null)}
                          style={{
                            fontFamily: 'Messinamodern, Arial, sans-serif',
                            fontSize: '1.75rem',
                            fontWeight: 400,
                            lineHeight: '120%',
                            letterSpacing: '0',
                            textTransform: 'none',
                            textDecoration: 'none',
                            color: 'var(--color-scheme-1-text-dark, #000)',
                            paddingTop: '0',
                            paddingBottom: '0',
                            transition: 'all .2s',
                            display: 'block',
                            opacity: hoveredMarketsItem === null || hoveredMarketsItem === 'investors' ? 1 : 0.3,
                          }}
                        >
                          For Investors
                        </a>
                        
                        <a 
                          href="https://www.marsmarkets.com/markets-for-artists"
                          onMouseEnter={() => setHoveredMarketsItem('artists')}
                          onMouseLeave={() => setHoveredMarketsItem(null)}
                          style={{
                            fontFamily: 'Messinamodern, Arial, sans-serif',
                            fontSize: '1.75rem',
                            fontWeight: 400,
                            lineHeight: '120%',
                            letterSpacing: '0',
                            textTransform: 'none',
                            textDecoration: 'none',
                            color: 'var(--color-scheme-1-text-dark, #000)',
                            paddingTop: '0',
                            paddingBottom: '0',
                            transition: 'all .2s',
                            display: 'block',
                            opacity: hoveredMarketsItem === null || hoveredMarketsItem === 'artists' ? 1 : 0.3,
                          }}
                        >
                          For Artists
                        </a>
                      </div>
                    </div>
                    
                    <p 
                      className="max-w-lg"
                      style={{
                        letterSpacing: '0',
                        textTransform: 'none',
                        fontFamily: 'Messinamodern, Arial, sans-serif',
                        fontSize: '.875rem',
                        fontWeight: 400,
                        lineHeight: '140%',
                        color: '#0a0a0a99',
                      }}
                    >
                      Tune into the world of alternative music investments.
                    </p>
                  </div>
                  
                  <div className="flex justify-center lg:justify-end">
                    
                    <div className="w-full overflow-hidden" style={{aspectRatio: '2'}}>
                      <img 
                        src="https://cdn.prod.website-files.com/68e16dd54a1f8b19623cc0d3/68e16dd54a1f8b19623cc1f5_Frame%25201597883650-p-2000.png" 
                        alt="Markets presentation showing data analysis"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* KNOWLEDGE Dropdown Block */}
          <AnimatePresence>
            {showKnowledgeDropdown && (
              <motion.div
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={dropdownVariants}
                className="hidden md:block absolute top-full left-0 right-0 w-full bg-nova-color shadow-lg z-40"
                onMouseEnter={() => setShowKnowledgeDropdown(true)}
                onMouseLeave={() => {
                  setShowKnowledgeDropdown(false);
                  setHoveredKnowledgeItem(null);
                }}
                style={{paddingLeft: "4%", paddingRight: "4%"}}
              >
                
                <div 
                  className="w-full mx-auto py-20 max-w-screen-2xl"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '.5fr .75fr',
                    gridColumnGap: '0rem',
                    gridRowGap: '0rem',
                    gridTemplateRows: 'auto',
                    gridAutoColumns: '1fr',
                    justifyContent: 'space-between',
                    paddingLeft: "5%",
                    paddingRight: "5%",
                  }}
                >
                  
                  <div 
                    style={{
                      display: 'flex',
                      flexFlow: 'column',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      width: '100%',
                      paddingRight: '2rem',
                      fontFamily: 'Messinasans, Arial, sans-serif',
                    }}
                  >
                    {/* KNOWLEDGE Label */}
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-600 font-medium" style={{
                        fontFamily: 'Messinasans, Arial, sans-serif',
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        letterSpacing: '2px',marginBottom: '1.5rem'
                      }}>
                        KNOWLEDGE
                      </div>
                      
                      {/* Navigation Links */}
                    
                      <div className="space-y-2 mt-4">
                        <a 
                          href="https://www.marsmarkets.com/knowledge/education-center"
                          onMouseEnter={() => setHoveredKnowledgeItem('education')}
                          onMouseLeave={() => setHoveredKnowledgeItem(null)}
                          style={{
                            fontFamily: 'Messinamodern, Arial, sans-serif',
                            fontSize: '1.75rem',
                            fontWeight: 400,
                            lineHeight: '120%',
                            letterSpacing: '0',
                            textTransform: 'none',
                            textDecoration: 'none',
                            color: 'var(--color-scheme-1-text-dark, #000)',
                            paddingTop: '0',
                            paddingBottom: '0',
                            transition: 'all .2s',
                            display: 'block',
                            opacity: hoveredKnowledgeItem === null || hoveredKnowledgeItem === 'education' ? 1 : 0.3,
                          }}
                        >
                          Education
                        </a>
                        <a 
                          href="https://www.marsmarkets.com/knowledge/insights"
                          onMouseEnter={() => setHoveredKnowledgeItem('insights')}
                          onMouseLeave={() => setHoveredKnowledgeItem(null)}
                          style={{
                            fontFamily: 'Messinamodern, Arial, sans-serif',
                            fontSize: '1.75rem',
                            fontWeight: 400,
                            lineHeight: '120%',
                            letterSpacing: '0',
                            textTransform: 'none',
                            textDecoration: 'none',
                            color: 'var(--color-scheme-1-text-dark, #000)',
                            paddingTop: '0',
                            paddingBottom: '0',
                            transition: 'all .2s',
                            display: 'block',
                            opacity: hoveredKnowledgeItem === null || hoveredKnowledgeItem === 'insights' ? 1 : 0.3,
                          }}
                        >
                          Insights
                        </a>
                        <a 
                          
                          href= "https://www.marsmarkets.com/knowledge/newsroom"
                          onMouseEnter={() => setHoveredKnowledgeItem('news')}
                          onMouseLeave={() => setHoveredKnowledgeItem(null)}
                          style={{
                            fontFamily: 'Messinamodern, Arial, sans-serif',
                            fontSize: '1.75rem',
                            fontWeight: 400,
                            lineHeight: '120%',
                            letterSpacing: '0',
                            textTransform: 'none',
                            textDecoration: 'none',
                            color: 'var(--color-scheme-1-text-dark, #000)',
                            paddingTop: '0',
                            paddingBottom: '0',
                            transition: 'all .2s',
                            display: 'block',
                            opacity: hoveredKnowledgeItem === null || hoveredKnowledgeItem === 'news' ? 1 : 0.3,
                          }}
                        >
                          News
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Side - Image (.75fr) */}
                  <div className="flex justify-center lg:justify-end">
                    
                    
                    {/* Knowledge Presentation Image */}
                    <div className="w-full overflow-hidden" style={{aspectRatio: '2'}}>
                      <img 
                        src="https://cdn.prod.website-files.com/68e16dd54a1f8b19623cc0d3/68ffa6cfd69e2de258e94d59_newsimage.webp" 
                        alt="Knowledge presentation showing education and insights"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  
                  {/* Old closing div - Commented out */}
                  {/* </div> */}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* LABS Dropdown Block */}
          <AnimatePresence>
            {showLabsDropdown && (
              <motion.div
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={dropdownVariants}
                className="hidden md:block absolute top-full left-0 right-0 w-full bg-nova-color shadow-lg z-40"
                onMouseEnter={() => setShowLabsDropdown(true)}
                onMouseLeave={() => {
                  setShowLabsDropdown(false);
                  setHoveredLabsItem(null);
                }}
                style={{paddingLeft: "4%", paddingRight: "4%"}}
              >
                
                <div 
                  className="w-full mx-auto py-20 max-w-screen-2xl"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '.5fr .75fr',
                    gridColumnGap: '0rem',
                    gridRowGap: '0rem',
                    gridTemplateRows: 'auto',
                    gridAutoColumns: '1fr',
                    justifyContent: 'space-between',
                    paddingLeft: "5%",
                    paddingRight: "5%",
                  }}
                >
                  
                  <div 
                    style={{
                      display: 'flex',
                      flexFlow: 'column',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      width: '100%',
                      paddingRight: '2rem',
                      fontFamily: 'Messinasans, Arial, sans-serif',
                    }}
                  >
                    {/* LABS Label and Content */}
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-600 font-medium" style={{
                        fontFamily: 'Messinasans, Arial, sans-serif',
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        letterSpacing: '2px',
                        marginBottom: '1.5rem',
                      }}>
                       MARS LABS
                      </div>
                      
                      
                      <div className="space-y-2 mt-4">
                        <a 
                          href="https://www.marsmarkets.com/mars-labs"
                          onMouseEnter={() => setHoveredLabsItem('what-is-labs')}
                          onMouseLeave={() => setHoveredLabsItem(null)}
                          style={{
                            fontFamily: 'Messinamodern, Arial, sans-serif',
                            fontSize: '1.75rem',
                            fontWeight: 400,
                            lineHeight: '120%',
                            letterSpacing: '0',
                            textTransform: 'none',
                            textDecoration: 'none',
                            color: 'var(--color-scheme-1-text-dark, #000)',
                            paddingTop: '0',
                            paddingBottom: '0',
                            transition: 'all .2s',
                            display: 'block',
                            opacity: hoveredLabsItem === null || hoveredLabsItem === 'what-is-labs' ? 1 : 0.3,
                          }}
                        >
                          What is Labs?
                        </a>
                        
                        {/* Nova and Nebula Headings */}
                        <a 
                          href="https://www.marsmarkets.com/mars-labs#nova"
                          onMouseEnter={() => setHoveredLabsItem('nova')}
                          onMouseLeave={() => setHoveredLabsItem(null)}
                          style={{
                            fontFamily: 'Messinamodern, Arial, sans-serif',
                            fontSize: '1.75rem',
                            fontWeight: 400,
                            lineHeight: '120%',
                            letterSpacing: '0',
                            textTransform: 'none',
                            textDecoration: 'none',
                            color: 'var(--color-scheme-1-text-dark, #000)',
                            paddingTop: '0',
                            paddingBottom: '0',
                            transition: 'all .2s',
                            display: 'block',
                            opacity: hoveredLabsItem === null || hoveredLabsItem === 'nova' ? 1 : 0.3,
                          }}
                        >
                          Nova
                        </a>
                        <a 
                          href="https://www.marsmarkets.com/mars-labs#nebula"
                          onMouseEnter={() => setHoveredLabsItem('nebula')}
                          onMouseLeave={() => setHoveredLabsItem(null)}
                          style={{
                            fontFamily: 'Messinamodern, Arial, sans-serif',
                            fontSize: '1.75rem',
                            fontWeight: 400,
                            lineHeight: '120%',
                            letterSpacing: '0',
                            textTransform: 'none',
                            textDecoration: 'none',
                            color: 'var(--color-scheme-1-text-dark, #000)',
                            paddingTop: '0',
                            paddingBottom: '0',
                            transition: 'all .2s',
                            display: 'block',
                            opacity: hoveredLabsItem === null || hoveredLabsItem === 'nebula' ? 1 : 0.3,
                          }}
                        >
                          Nebula
                        </a>
                      </div>
                    </div>
                    
                    {/* Description Text */}
                    <p className="text-base lg:text-lg text-gray-700 max-w-lg" 
                      style={{
                        letterSpacing: '0',
                        textTransform: 'none',
                        fontFamily: 'Messinamodern, Arial, sans-serif',
                        fontSize: '.875rem',
                        fontWeight: 400,
                        lineHeight: '140%',
                        color: '#0a0a0a99',
                      }}
                    >
                      Tools to manage, distribute, and monetize music.
                    </p>
                  </div>
                  
                  {/* Right Side - Image (.75fr) */}
                  <div className="flex justify-center lg:justify-end">
                    
                    
                    {/* Labs Presentation Image */}
                    <div className="w-full overflow-hidden" style={{aspectRatio: '2'}}>
                      <img 
                        src="https://cdn.prod.website-files.com/68e16dd54a1f8b19623cc0d3/68ffa71ef19420b2e6e712eb_marslabs.webp" 
                        alt="Labs presentation showing music production tools"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  
                  {/* Old closing div - Commented out */}
                  {/* </div> */}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

       {/* Mobile Navigation Menu */}
       <AnimatePresence>
         {isOpen && (
           <motion.div
             initial="hidden"
             animate="visible"
             exit="exit"
             variants={mobileMenuVariants}
             className={`md:hidden fixed left-0 right-0 w-full overflow-hidden ${
               isAuthenticated ? 'bg-nova-color' : 'bg-black'
             }`}
             style={{ top: '70px', zIndex: 40 }}
           >
           <div className={`w-full overflow-y-auto ${
             isAuthenticated ? 'bg-nova-color' : 'bg-black'
           }`} style = {{
             paddingTop: isAuthenticated ? '2rem' : '5rem',
             paddingLeft: isAuthenticated ? '1.5rem' : '0',
             paddingRight: isAuthenticated ? '1.5rem' : '0',
             height: '100%'
           }}>
             {!isAuthenticated ? (
               <>
                 {/* New Mobile Navigation: MARKETS, KNOWLEDGE, MARS LABS */}
                 {/* MARKETS Dropdown */}
                 
                 <div className="w-full">
                   <button 
                     className="flex items-center justify-between w-full py-4 px-4 text-white"
                     onClick={() => setShowMarketsDropdown(!showMarketsDropdown)}
                     style={{
                       fontFamily: 'Messinamodern,Arial,sans-serif',
                       fontSize: '1.75rem',
                       lineHeight: '120%',
                       fontWeight: 400,
                     }}
                   >
                     <span>Markets</span>
                     {showMarketsDropdown ? (
                       <ChevronUp size={20} className="text-white" />
                     ) : (
                       <ChevronDown size={20} className="text-white" />
                     )}
                   </button>
                   <AnimatePresence>
                     {showMarketsDropdown && (
                       <motion.div
                         initial="hidden"
                         animate="visible"
                         exit="exit"
                         variants={mobileDropdownVariants}
                         className="overflow-hidden"
                       >
                         <div className="px-4 pb-4 space-y-3">
                           <a 
                             href="https://www.marsmarkets.com/markets-for-investors"
                             className="block text-white"
                             onClick={() => setIsOpen(false)}
                             style={{
                               fontFamily: 'Messinamodern, Arial, sans-serif',
                               fontSize: '1rem',
                               fontWeight: 400,
                               paddingLeft: '1rem',
                               color: '#fff9',
                             }}
                           >
                             For Investors
                           </a>
                           <a 
                             href="https://www.marsmarkets.com/markets-for-artists"
                             className="block text-white"
                             onClick={() => setIsOpen(false)}
                             style={{
                               fontFamily: 'Messinamodern, Arial, sans-serif',
                               fontSize: '1rem',
                               fontWeight: 400,
                               paddingLeft: '1rem',
                               color: '#fff9',
                             }}
                           >
                             For Artists
                           </a>
                         </div>
                       </motion.div>
                     )}
                   </AnimatePresence>
                   <div className="pl-4 pr-4">
                    <div className="border-t border-gray-600" style = {{borderTopWidth: '1px', borderColor: 'white'}}></div>

                   </div>
                 </div>

                 {/* KNOWLEDGE Dropdown */}
                 {/* Old mobile design - Commented out */}
                 
                 <div className="w-full">
                   <button 
                     className="flex items-center justify-between w-full py-4 px-4 text-white"
                     onClick={() => setShowKnowledgeDropdown(!showKnowledgeDropdown)}
                     style={{
                       fontFamily: 'Messinamodern,Arial,sans-serif',
                       fontSize: '1.75rem',
                       lineHeight: '120%',
                       fontWeight: 400,
                     }}
                   >
                     <span>Knowledge</span>
                     {showKnowledgeDropdown ? (
                       <ChevronUp size={20} className="text-white" />
                     ) : (
                       <ChevronDown size={20} className="text-white" />
                     )}
                   </button>
                   <AnimatePresence>
                     {showKnowledgeDropdown && (
                       <motion.div
                         initial="hidden"
                         animate="visible"
                         exit="exit"
                         variants={mobileDropdownVariants}
                         className="overflow-hidden"
                       >
                         <div className="px-4 pb-4 space-y-3">
                           <a 
                             href="https://www.marsmarkets.com/knowledge/education-center"
                             className="block text-white"
                             onClick={() => setIsOpen(false)}
                             style={{
                               fontFamily: 'Messinamodern, Arial, sans-serif',
                               fontSize: '1rem',
                               fontWeight: 400,
                               paddingLeft: '1rem',
                               color: '#fff9',
                             }}
                           >
                             Education
                           </a>
                           <a 
                             href="https://www.marsmarkets.com/knowledge/insights"
                             className="block text-white"
                             onClick={() => setIsOpen(false)}
                             style={{
                               fontFamily: 'Messinamodern, Arial, sans-serif',
                               fontSize: '1rem',
                               fontWeight: 400,
                               paddingLeft: '1rem',
                               color: '#fff9',
                             }}
                           >
                             Insights
                           </a>
                           <a 
                             href="https://www.marsmarkets.com/knowledge/newsroom"
                             className="block text-white"
                             onClick={() => setIsOpen(false)}
                             style={{
                               fontFamily: 'Messinamodern, Arial, sans-serif',
                               fontSize: '1rem',
                               fontWeight: 400,
                               paddingLeft: '1rem',
                               color: '#fff9',
                             }}
                           >
                             News
                           </a>
                         </div>
                       </motion.div>
                     )}
                   </AnimatePresence>
                   <div className="pl-4 pr-4">
                    <div className="border-t border-gray-600" style = {{borderTopWidth: '1px', borderColor: 'white'}}></div>

                   </div>
                 </div>

                 {/* MARS LABS Dropdown */}
                 {/* Old mobile design - Commented out */}
                 
                 <div className="w-full">
                   <button 
                     className="flex items-center justify-between w-full py-4 px-4 text-white"
                     onClick={() => setShowLabsDropdown(!showLabsDropdown)}
                     style={{
                       fontFamily: 'Messinamodern,Arial,sans-serif',
                       fontSize: '1.75rem',
                       lineHeight: '120%',
                       fontWeight: 400,
                     }}
                   >
                     <span>Mars Labs</span>
                     {showLabsDropdown ? (
                       <ChevronUp size={20} className="text-white" />
                     ) : (
                       <ChevronDown size={20} className="text-white" />
                     )}
                   </button>
                   <AnimatePresence>
                     {showLabsDropdown && (
                       <motion.div
                         initial="hidden"
                         animate="visible"
                         exit="exit"
                         variants={mobileDropdownVariants}
                         className="overflow-hidden"
                       >
                         <div className="px-4 pb-4 space-y-3">
                           <a 
                             href="https://www.marsmarkets.com/mars-labs"
                             className="block text-white"
                             onClick={() => setIsOpen(false)}
                             style={{
                               fontFamily: 'Messinamodern, Arial, sans-serif',
                               fontSize: '1rem',
                               fontWeight: 400,
                               paddingLeft: '1rem',
                               color: '#fff9',
                             }}
                           >
                             What is Labs?
                           </a>
                           <a 
                             href="https://www.marsmarkets.com/mars-labs#nova"
                             className="block text-white"
                             onClick={() => setIsOpen(false)}
                             style={{
                               fontFamily: 'Messinamodern, Arial, sans-serif',
                               fontSize: '1rem',
                               fontWeight: 400,
                               paddingLeft: '1rem',
                               color: '#fff9',
                             }}
                           >
                             Nova
                           </a>
                           <a 
                             href="https://www.marsmarkets.com/mars-labs#nebula"
                             className="block text-white"
                             onClick={() => setIsOpen(false)}
                             style={{
                               fontFamily: 'Messinamodern, Arial, sans-serif',
                               fontSize: '1rem',
                               fontWeight: 400,
                               paddingLeft: '1rem',
                               color: '#fff9',
                             }}
                           >
                             Nebula
                           </a>
                         </div>
                       </motion.div>
                     )}
                   </AnimatePresence>
                   <div className="pl-4 pr-4">
                    <div className="border-t border-gray-600" style = {{borderTopWidth: '1px', borderColor: 'white'}}></div>

                   </div>
                 </div>

                
               </>
             ) : (
               <>
                 <button
                   className="flex items-center justify-center gap-2 bg-black text-white px-4 py-3 rounded-full signout-btn w-full"
                   onClick={() => {
                     handleSignOut();
                     setIsOpen(false);
                   }}
                 >
                   <svg width={20} height={20} viewBox="0 0 20 20" fill="none">
                     <path d="M4.1665 16.6667V15.8333C4.1665 12.6117 6.77818 10 9.99984 10C13.2215 10 15.8332 12.6117 15.8332 15.8333V16.6667" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                     <path d="M9.99984 9.99992C11.8408 9.99992 13.3332 8.5075 13.3332 6.66659C13.3332 4.82564 11.8408 3.33325 9.99984 3.33325C8.15889 3.33325 6.6665 4.82564 6.6665 6.66659C6.6665 8.5075 8.15889 9.99992 9.99984 9.99992Z" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                   </svg>
                   <span>SIGN OUT</span>
                 </button>
               </>
             )}
           </div>
           
           {/* Fixed JOIN WAITLIST Button at Bottom - Mobile Only */}
           {!isAuthenticated && (
             <div className="md:hidden fixed bottom-0 left-0 right-0 bg-black p-4 z-50">
               <Link to="/login">
                 <button 
                   className="bg-white text-black px-4 py-3 rounded-full text-sm w-full font-medium uppercase"
                   onClick={() => setIsOpen(false)}
                   style={{
                     fontFamily: 'Messinasans,Arial,sans-serif',
                     fontSize: '0.625rem',
                     lineHeight: '140%',
                     fontWeight: 600,
                     letterSpacing: '2px',
                     borderRadius: '99px',
                     padding: '0.8rem 1.5rem',
                     border: 'none'
                   }}
                 >
                   JOIN WAITLIST
                 </button>
               </Link>
             </div>
           )}
           </motion.div>
         )}
       </AnimatePresence>
    </header>
    </>
  );
};

export default Navbar;




